/**
 * `GET /eventFieldConfig/event-types` -> the shape `toEvents` resolves icons
 * against (FIN-13583).
 *
 * The response is the settings screen's own list — one row per configured event
 * type, each with `eventCategory`, `technicalValue` and an optional `iconKey`:
 *
 *   [{ id, eventCategory: 'ACTIVITY', technicalValue: 'LOGIN',
 *      displayName: 'Login', iconKey: 'log-in', isDefault: false }, …]
 *
 * and this reduces it to
 *
 *   { ACTIVITY:    { byType: { LOGIN: 'log-in' }, fallback: 'piggy-bank' },
 *     TRANSACTION: { byType: {},                  fallback: 'piggy-bank' } }
 *
 * THE SYSTEM DEFAULT ROW BECOMES `fallback`, NOT AN ENTRY IN `byType`. It is
 * identified by `isSystemType` (the BE's `isSystem`, i.e. the reserved
 * `technical_value = 'default'`), NOT by `isDefault` (FIN-13583 reopen).
 * `isDefault` is the settings screen's movable "Set as Default" star: it leaves
 * the System Default row the moment another type is starred, or can be turned
 * off entirely, and the System Default icon then never applied.
 *
 * ROWS WHOSE `iconKey` IS MISSING — OR IS NOT ONE THE REGISTRY CAN DRAW — ARE
 * OMITTED ENTIRELY rather than stored as null. Most
 * types have never had an icon chosen, and keeping them would force every reader
 * to re-check for null; absence already means "nothing configured", which is
 * exactly what the precedence chain in toEvents wants to see.
 *
 * TOTALLY DEFENSIVE. This runs against a response the timeline does not control
 * and does not need: a 404 (the controller is not mounted on every deployment),
 * a failure, or a shape we do not recognise all reduce to `{}`, and the timeline
 * draws its built-in glyphs exactly as it did before. Icons are decoration on an
 * evidence screen — they must never be able to empty it.
 */
import { EVENT_ICONS, canonicalIconKey } from '../eventIcons';
import { isSystemType } from '../../../../../settings/EventFields/constants';

const CATEGORIES = ['ACTIVITY', 'TRANSACTION'];

const textOrUndefined = (value) =>
  typeof value === 'string' && value.trim() ? value.trim() : undefined;

/**
 * A KEY THE REGISTRY CANNOT DRAW IS NOT A CONFIGURED ICON (raised in review).
 *
 * `icon_key` is persisted free text: legacy rows, a renamed registry entry or a
 * hand-edited row can all hold something `EVENT_ICONS` has never heard of. Such
 * a key used to be accepted here purely for being non-blank, and `toEvents`
 * ranks a configured key ABOVE the built-in per-type glyph — so an unreadable
 * key did not fall through to the glyph, it REPLACED it, and `getEventIcon`
 * returned the generic apartment icon. A Login row carrying a stale key lost its
 * arrow; before FIN-13583 it drew correctly.
 *
 * Dropping the key instead restores the intended fallthrough, because absence is
 * already the signal the precedence chain reads as "nothing configured". The
 * check runs on the CANONICAL key so the legacy aliases still resolve — they are
 * exactly the old keys this must not throw away.
 */
const drawableIconKey = (key) => {
  const canonical = canonicalIconKey(key);
  return canonical && EVENT_ICONS[canonical] ? key : undefined;
};

export const toIconConfig = (eventTypes) => {
  const rows = Array.isArray(eventTypes) ? eventTypes : eventTypes?.data;
  if (!Array.isArray(rows)) return {};

  const config = {};

  rows.forEach((row) => {
    const category = textOrUndefined(row?.eventCategory);
    const iconKey = drawableIconKey(textOrUndefined(row?.iconKey));
    if (!category || !CATEGORIES.includes(category) || !iconKey) return;

    // The bucket is created only once there is something to put in it, so a row
    // we end up ignoring cannot leave an empty category behind — `{}` has to mean
    // "nothing is configured" for the timeline's precedence chain to read simply.
    const technicalValue = textOrUndefined(row?.technicalValue);
    const isSystem = isSystemType(row);
    if (!isSystem && !technicalValue) return;

    if (!config[category]) config[category] = { byType: {} };

    if (isSystem) config[category].fallback = iconKey;
    else config[category].byType[technicalValue] = iconKey;
  });

  return config;
};

export default toIconConfig;
